/**
 * Helpers para extrair tenant a partir do hostname (web)
 * ou do JWT (após login).
 */

import { SUPER_ADMIN_SUBDOMAIN } from './constants';

/**
 * Lista de root domains que NAO são tenant.
 *
 * Quando o hostname **bate exatamente** com um destes ou é um
 * subdominio reservado (`gestão`, `app`, etc.) destes, retorna null.
 *
 * Quando o hostname é `<algo>.<root>`, `<algo>` é tratado como tenant.
 *
 * Para adicionar um novo root domain (ex: ao registrar novo dominio
 * de operação), inclua aqui em ordem de mais especifico para mais geral.
 */
const ROOT_DOMAINS = [
  'wash.octasistemas.com', // octa, dominio em uso
  'octawash.com.br',
  'octawash.com',
] as const;

/**
 * Subdominios reservados que NAO são tenant (mesmo aparecendo como
 * primeiro label antes de um root domain).
 */
const RESERVADOS = new Set<string>([
  'www',
  'gestao',
  'gestão',
  'app',
  'api',
  SUPER_ADMIN_SUBDOMAIN,
]);

/**
 * Extrai o subdominio de tenant a partir do hostname.
 *
 * Exemplos:
 *   acme.gestão.octawash.com.br      -> 'acme'
 *   acme.octawash.com.br             -> 'acme'
 *   acme.wash.octasistemas.com       -> 'acme'
 *   acme.localhost                   -> 'acme' (DEV)
 *   localhost                        -> null (sem tenant)
 *   wash.octasistemas.com            -> null (root, usar default)
 *   gestão.octawash.com.br           -> null (super-admin login)
 *   gestão.wash.octasistemas.com     -> null (super-admin login)
 *   octawash.com.br                  -> null (root domain)
 */
export function extrairSubdominioTenant(hostname: string): string | null {
  const lower = hostname.toLowerCase().trim();
  if (!lower) return null;

  // 1. Hostname == root domain exato -> sem tenant
  for (const root of ROOT_DOMAINS) {
    if (lower === root) return null;
  }

  // 2. Hostname termina em .<root> -> primeiro label do prefix é candidato
  for (const root of ROOT_DOMAINS) {
    const sufixo = `.${root}`;
    if (lower.endsWith(sufixo)) {
      const prefix = lower.slice(0, -sufixo.length);
      if (!prefix) return null;
      const primeiroLabel = prefix.split('.')[0];
      if (!primeiroLabel) return null;
      if (RESERVADOS.has(primeiroLabel)) return null;
      return primeiroLabel;
    }
  }

  // 3. Fallback (DEV / hostnames não reconhecidos): lógica antiga
  //    primeiro label + checagem de reservados
  const partes = lower.split('.');
  if (partes.length < 2) return null;
  const primeiro = partes[0];
  if (!primeiro) return null;
  if (primeiro === 'localhost') return null;
  if (RESERVADOS.has(primeiro)) return null;
  return primeiro;
}

/**
 * Verifica se hostname está dentro de um root domain conhecido do OctaWash.
 * Útil pra decidir entre lookup sync (subdomínio) ou async (custom domain).
 *
 * Exemplo:
 *   isHostnameOctaWash('acme.wash.octasistemas.com')          -> true
 *   isHostnameOctaWash('wash.redemontecarlo.com.br')          -> false (custom domain)
 *   isHostnameOctaWash('localhost')                            -> true (DEV)
 *   isHostnameOctaWash('acme.localhost')                       -> true (DEV)
 */
export function isHostnameOctaWash(hostname: string): boolean {
  const lower = hostname.toLowerCase().trim();
  if (!lower) return false;
  if (lower === 'localhost' || lower.endsWith('.localhost')) return true;
  for (const root of ROOT_DOMAINS) {
    if (lower === root || lower.endsWith(`.${root}`)) return true;
  }
  return false;
}

/**
 * Resultado da resolução de tenant via RPC.
 */
export interface TenantHostnameResult {
  seq_tenant: number;
  cod_tenant: string;
  nom_tenant: string;
  des_subdominio: string;
  des_dominio_proprio_admin: string | null;
  des_dominio_proprio_cliente: string | null;
  cod_portal: 'admin' | 'cliente' | 'subdominio';
}

/**
 * Le o tenant_id da claim app_metadata do JWT decodificado.
 * Retorna null se não houver claim válida.
 */
export function tenantIdDoJwt(appMetadata: unknown): number | null {
  if (typeof appMetadata !== 'object' || appMetadata === null) return null;
  const meta = appMetadata as Record<string, unknown>;
  const valor = meta.tenant_id;
  if (typeof valor === 'number') return valor;
  if (typeof valor === 'string') {
    const n = Number.parseInt(valor, 10);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/**
 * Le o role do JWT decodificado. Tipico: 'cliente' | 'admin' | 'tech_admin' | 'super_admin'
 */
export function roleDoJwt(appMetadata: unknown): string | null {
  if (typeof appMetadata !== 'object' || appMetadata === null) return null;
  const meta = appMetadata as Record<string, unknown>;
  const valor = meta.role;
  return typeof valor === 'string' ? valor : null;
}
